import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { EyeIcon, EyeSlashIcon, KeyIcon, XIcon } from '@phosphor-icons/react';

import { useSetUserPassword } from '../api/admin-api';

import { adminButtonSx, adminColors } from './adminTheme';

const MIN_LENGTH = 8;

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  /** Display name / email shown in the subtitle. */
  userLabel: string;
}

/** Modal that sets a user's password, with a temporary/permanent toggle
 * mirroring the Keycloak admin console. */
export const SetPasswordModal: FC<Props> = observer(({ open, onClose, userId, userLabel }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const btn = adminButtonSx(theme);
  const { t } = useTrans();
  const setPassword = useSetUserPassword();

  const [password, setPassword_] = useState('');
  const [temporary, setTemporary] = useState(true);
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPassword_('');
    setTemporary(true);
    setShow(false);
    setSubmitted(false);
  }, [open]);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const passwordError = submitted && (password.length === 0 || tooShort);

  const handleSubmit = () => {
    setSubmitted(true);
    if (password.length < MIN_LENGTH) return;
    setPassword.mutate({ userId, body: { password, temporary } }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden' } }}>
      <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.625, borderBottom: `1px solid ${c.border}` }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, backgroundColor: theme.palette.grey[200], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <KeyIcon size={20} color={c.violet} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: c.ink }}>
            {t('app.admin.setPassword.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.125 }} noWrap>
            {t('app.admin.setPassword.subtitle', { user: userLabel })}
          </Typography>
        </Box>
        <Button onClick={onClose} sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}>
          <XIcon size={16} />
        </Button>
      </Box>

      <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label={t('app.admin.setPassword.password')}
          type={show ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword_(e.target.value)}
          error={passwordError}
          helperText={passwordError ? t('app.admin.setPassword.validation.minLength', { n: MIN_LENGTH }) : t('app.admin.setPassword.hint', { n: MIN_LENGTH })}
          size="small"
          fullWidth
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShow((v) => !v)} edge="end" size="small">
                  {show ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ backgroundColor: theme.palette.grey[200], borderRadius: 2.5, px: 1.75, py: 1.25 }}>
          <FormControlLabel
            sx={{ m: 0, display: 'flex', justifyContent: 'space-between', width: '100%' }}
            labelPlacement="start"
            control={<Switch checked={temporary} onChange={(e) => setTemporary(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.ink }}>
                {t('app.admin.setPassword.temporary')}
              </Typography>
            }
          />
          <Typography sx={{ fontSize: 11.5, color: c.sub, lineHeight: 1.5, mt: 0.5 }}>
            {temporary
              ? t('app.admin.setPassword.temporaryOn')
              : t('app.admin.setPassword.temporaryOff')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25, borderTop: `1px solid ${c.border}` }}>
        <Button onClick={onClose} sx={{ ...btn.neutral, px: 2, borderRadius: 2.75 }}>
          {t('app.admin.setPassword.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={setPassword.isPending}
          startIcon={<KeyIcon size={16} />}
          sx={{ ...btn.solid, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.setPassword.submit')}
        </Button>
      </Box>
    </Dialog>
  );
});
